import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { makeWorldCrs, toLatLng, worldRectToBounds } from '../geo/crs';
import { chunkAt } from '../geo/measure';
import { num } from '../geo/format';
import { useCurrentMap, useStore } from '../store/store';
import { sampleHeight } from '../store/data';
import { createMapControllers, createPanes } from './controllers';
import { useCursor, useMeasure } from './cursor';

// Leaflet guesses its default icon path from the CSS URL, which bundlers break; point it at the assets.
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

export function MapView() {
  const el = useRef<HTMLDivElement>(null);
  const map = useCurrentMap();
  const mapId = map?.id;
  const rectKey = map ? JSON.stringify(map.worldRect) : '';

  useEffect(() => {
    const m = useStore.getState().maps.find((x) => x.id === mapId);
    if (!m || !el.current) return;
    const r = m.worldRect;
    const pad = Math.max(r.maxX - r.minX, r.maxZ - r.minZ) * 0.15;
    const lmap = L.map(el.current, {
      crs: makeWorldCrs(r),
      minZoom: -5,
      maxZoom: 5,
      zoomSnap: 0.25,
      zoomDelta: 0.5,
      wheelPxPerZoomLevel: 90,
      attributionControl: false,
      boxZoom: true,
      doubleClickZoom: false,
      maxBounds: worldRectToBounds({
        minX: r.minX - pad,
        minZ: r.minZ - pad,
        maxX: r.maxX + pad,
        maxZ: r.maxZ + pad,
      }),
    });
    createPanes(lmap);
    L.control.scale({ imperial: false, position: 'bottomright', maxWidth: 160 }).addTo(lmap);
    const v = m.defaultView;
    if (v) lmap.setView(toLatLng(v.x, v.z), v.zoom);
    else lmap.fitBounds(worldRectToBounds(m.landExtent ?? r));
    const dispose = createMapControllers(lmap);
    const onZoom = () => {
      const c = lmap.getContainer().classList;
      c.toggle('zoomed-out', lmap.getZoom() < -0.75); // hides point labels
      c.toggle('far-out', lmap.getZoom() < -1.75); // hides chunk labels
    };
    lmap.on('zoomend', onZoom);
    onZoom();
    // The container changes size when the inspector opens and when the data view hides the map.
    const resize = new ResizeObserver(() => lmap.invalidateSize({ pan: false }));
    resize.observe(el.current);
    return () => {
      resize.disconnect();
      dispose();
      lmap.remove();
    };
  }, [mapId, rectKey]);

  return (
    <div className="map-wrap">
      <div ref={el} className="map" />
      <ToolHint />
      <Readout />
    </div>
  );
}

function Readout() {
  const { x, z, inside } = useCursor();
  const map = useCurrentMap();
  const hasHeights = useStore((s) => !!s.heightmap);
  if (!inside || !map) return <div className="readout muted">—</div>;
  const origin = map.origin ?? { x: map.worldRect.minX, z: map.worldRect.minZ };
  const chunk = chunkAt(x, z, origin, map.chunkSize, map.chunks ?? []);
  const y = hasHeights ? sampleHeight(x, z) : null;
  return (
    <div className="readout mono">
      X {num(x, 1)}&nbsp;&nbsp;Z {num(z, 1)}
      {y !== null && <>&nbsp;&nbsp;Y {num(y, 1)}</>}
      {chunk && <span className="muted"> · {chunk.sceneName}</span>}
    </div>
  );
}

function ToolHint() {
  const { t } = useTranslation();
  const tool = useStore((s) => s.tool);
  const editing = useStore((s) => s.editingShapeId);
  const measure = useMeasure();
  let text: string | null = null;
  if (tool.kind === 'draw') text = t(`tools.drawHint.${tool.geometry}`);
  else if (tool.kind === 'measure')
    text =
      measure.points > 0
        ? t('tools.measureValue', {
            length: num(measure.length, 1),
            area: measure.area !== null ? num(measure.area, 0) : '—',
          })
        : t('tools.measureHint');
  else if (tool.kind === 'pick') text = t('tools.pickHint');
  else if (tool.kind === 'calibrate')
    text = t(tool.step === 'image' ? 'calibration.clickImage' : 'calibration.clickWorld');
  else if (editing) text = t('tools.editHint');
  if (!text) return null;
  return <div className="tool-hint">{text}</div>;
}
